/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { pluck } from 'underscore'
import Modal from '../../Modal/index'
import { useServiceContext } from '../../../root'
import { getSubTriggerNameOrPH } from './utils'
import type { PackageData, PackageOptions } from './types'

interface EditPackageReviewProps {
  packageData: PackageData | null
  options: PackageOptions | null
  nextStep: (packageData: PackageData, optionsData: PackageOptions, nextStepItem: 'STEP1' | 'STEP2' | 'STEP3') => void
  doSave: any
  doClose: () => void
}

const EditPackageReview: React.FC<EditPackageReviewProps> = ({ packageData, options, nextStep, doSave, doClose }) => {
  const context = useServiceContext()
  const translate = context.service.filter.translate
  const [applyButtonView, setApplyButtonView] = useState(false)

  return (
    <>
      <Modal.Body>
        <div className="fs12 edit-package-review">
          <div className="form-group w100p mt10">
            <div>{translate('SETTINGS_PACKAGE_EDIT_REVIEW_INTRO_TXT')}</div>
          </div>
          <div className="form-group md-form-group w100p level-spacer">
            <label htmlFor="ae2e8461-a710-4b83-9ba3-a2bd9872a1d2">{translate('SETTINGS_PACKAGE_NAME')}</label>
            <input
              type="text"
              className="md-input"
              disabled
              id="ae2e8461-a710-4b83-9ba3-a2bd9872a1d2"
              name="packageName"
              value={packageData?.name || ''}
            />
          </div>
          <div className="form-group md-form-group w100p level-spacer">
            <label htmlFor="03f4444c-70b4-4573-a7b6-256f512d0850">{translate('EMTF_LEVEL')}</label>
            <input
              type="text"
              className="md-input"
              disabled
              id="03f4444c-70b4-4573-a7b6-256f512d0850"
              name="etmfLevel"
              value={packageData?.level ? translate('EMTF_MD_' + packageData.level) : ''}
            />
          </div>
          <div className="form-group md-form-group w100p level-spacer">
            <label htmlFor="be19ea2b-f59f-4105-97c8-faad9242b108">{translate('FILE_PLAN')}</label>
            <input
              type="text"
              className="md-input"
              disabled
              id="be19ea2b-f59f-4105-97c8-faad9242b108"
              name="filePlan"
              value={packageData?.filePlanName || ''}
            />
          </div>
          {packageData?.autoApplyPackage && (
            <div className="form-group md-form-group w100p level-spacer">
              <label>{translate('AUTO_APPLY_PACKAGE')}</label>
              <div className="fs12 selected-entries">
                <div>
                  {`${translate('AUTO_APPLY_PACKAGE_TRIGGER')}: ${translate('TRIGGER_' + (typeof packageData?.triggerName === 'object' && packageData?.triggerName !== null ? packageData.triggerName.value : packageData?.triggerName || ''))}`}
                </div>
                <div>
                  {`${translate(
                    getSubTriggerNameOrPH(
                      typeof packageData?.triggerName === 'object' && packageData?.triggerName !== null
                        ? packageData.triggerName.value
                        : packageData?.triggerName || '',
                      false
                    )
                  )}: ${
                    Array.isArray(packageData?.triggerDetails)
                      ? pluck(packageData.triggerDetails, 'label')?.toString() || ''
                      : packageData?.triggerDetails || ''
                  }`}
                </div>
              </div>
            </div>
          )}
          <div className="level-spacer">
            <label>{translate('SETTINGS_PACKAGE_ARTIFACTS')}</label>
            <div className="selected-entries">
              {options?.selectedFPEntries?.map((item, index) => (
                <div key={index}>{item?.name}</div>
              ))}
            </div>
          </div>
          <div className="form-group w100p mb0">
            <b>{translate('SETTINGS_PACKAGE_EDIT_REVIEW_CONFIRMATION_TXT')}</b>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          <button
            id="86ae1e13-3726-4d54-9949-a0f11e877982"
            data-action="cancel"
            className="btn btn-link text-capitalize"
            onClick={doClose}
            type="button"
          >
            {translate('CANCEL')}
          </button>
          <button
            className="btn btn-second text-capitalize"
            id="b04319cf-8bfa-4f47-9a7d-2497e781393e"
            onClick={() => nextStep(packageData || ({} as PackageData), options || ({} as PackageOptions), 'STEP2')}
          >
            {translate('BACK')}
          </button>
          <div className="action-button-container" style={{ position: 'relative', display: 'inline-block' }}>
            <div className="action-button-wrapper">
              <button
                id="e630ada5-baec-458b-b6a2-a3e8ba8d640b"
                data-action="save"
                onClick={() => {
                  doSave()
                }}
                className="btn btn-primary ml5 text-capitalize save-button"
                type="submit"
              >
                {translate('SETTINGS_PACKAGE_SAVE_BTN')}
              </button>
              <button
                id="9d53e508-2892-44a1-bb12-62dd95343bb4"
                data-action="save"
                onClick={() => {
                  setApplyButtonView((value) => !value)
                }}
                className="btn btn-primary text-capitalize toggle-button dropdown-toggle"
                type="button"
              >
                <span className="caret"></span>
                <span className="sr-only">Split button!</span>
              </button>
            </div>
            {applyButtonView && (
              <div className="apply-button-container">
                <button
                  className="text-capitalize apply-package-button btn btn-default"
                  id="9833a81e-6bd8-4e84-a3dc-d0f109109784"
                  onClick={() => {
                    doSave(true)
                    setApplyButtonView(false)
                  }}
                  type="button"
                >
                  {translate('SETTINGS_PACKAGE_SAVE_APPLY_BTN')}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal.Footer>
    </>
  )
}

export default EditPackageReview
